import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, LayoutGrid, Trash2, LogOut, HelpCircle, Clock, Sparkles, FolderKanban, LifeBuoy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NewProjectDialog } from "@/components/NewProjectDialog";
import { HelpCenter } from "@/components/help/HelpCenter";
import { SupportCenter } from "@/components/support/SupportCenter";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { api } from "@/api/client";
import { Logo } from "@/components/Logo";
import { Wordmark } from "@/components/Wordmark";
import { UpgradeDialog } from "@/components/UpgradeDialog";
import { BlueprintBackground } from "@/components/BlueprintBackground";
import { useAuth } from "@/contexts/AuthContext";

function formatDate(iso) {
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

export default function Dashboard() {
  const [projects, setProjects] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const load = () => api.get("/projects").then((res) => setProjects(res.data));
  useEffect(() => {
    load();
  }, []);

  const handleDelete = async () => {
    await api.delete(`/projects/${deleteTarget.id}`);
    setDeleteTarget(null);
    load();
  };

  const sortedProjects = useMemo(
    () => [...projects].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)),
    [projects]
  );
  const mostRecent = sortedProjects[0];

  return (
    <div className="min-h-screen text-slate-100" data-testid="dashboard-page">
      <BlueprintBackground />
      <header className="sticky top-0 z-40 border-b border-slate-800/80 bg-[#0F172A]/90 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Logo size={24} />
            <Wordmark className="font-heading text-lg sm:text-xl font-bold tracking-tight text-white" accentClassName="text-blue-400" />
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 bg-slate-900/60 p-1.5 rounded-xl border border-slate-800/80">
            {user?.plan === "pro" ? (
              <span
                data-testid="pro-plan-badge"
                className="text-xs font-mono font-semibold text-blue-400 bg-blue-950/80 border border-blue-500/30 px-2.5 py-1.5 rounded-lg whitespace-nowrap"
              >
                PRO
              </span>
            ) : (
              <button
                data-testid="upgrade-to-pro-button"
                onClick={() => {
                  setUpgradeReason(null);
                  setUpgradeOpen(true);
                }}
                className="text-xs font-semibold text-blue-300 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/40 px-2.5 sm:px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap"
              >
                Upgrade
              </button>
            )}
            <Button
              data-testid="new-project-button"
              onClick={() => setDialogOpen(true)}
              className="bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_15px_-3px_rgba(37,99,235,0.5)] px-3 sm:px-4"
            >
              <Plus size={16} className="sm:mr-1.5" /> <span className="hidden sm:inline">New Project</span>
            </Button>
            <button
              data-testid="open-help-center-button"
              onClick={() => setHelpOpen(true)}
              className="p-2 text-slate-400 hover:text-blue-400 hover:bg-slate-800/80 rounded-lg transition-colors"
              title="Help Center"
            >
              <HelpCircle size={18} />
            </button>
            <button
              data-testid="open-support-center-button"
              onClick={() => setSupportOpen(true)}
              className="p-2 text-slate-400 hover:text-blue-400 hover:bg-slate-800/80 rounded-lg transition-colors"
              title="Contact Support"
            >
              <LifeBuoy size={18} />
            </button>
            <button
              data-testid="logout-button"
              onClick={handleLogout}
              className="flex items-center gap-1.5 p-2 text-slate-400 hover:text-rose-400 hover:bg-rose-950/30 rounded-lg transition-colors text-sm"
              title="Log Out"
            >
              <LogOut size={15} /> <span className="hidden sm:inline">Log Out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10 relative z-10">
        <h2
          className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-white mb-1"
          data-testid="dashboard-welcome-heading"
        >
          Model &amp; System Registry
        </h2>
        <p className="text-slate-400 mb-6 sm:mb-8 text-sm">Model, import, and talk to your ArchiMate enterprise architectures.</p>

        <div
          className="bg-slate-900/70 border border-slate-800/80 rounded-xl p-3 sm:p-4 mb-6 sm:mb-8 grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-6 backdrop-blur-sm"
          data-testid="dashboard-context-strip"
        >
          <div>
            <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1">
              <LayoutGrid size={12} /> Registered Models
            </div>
            <div className="text-xl sm:text-2xl font-heading font-bold text-white" data-testid="context-project-count">
              {projects.length}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1">
              <Sparkles size={12} /> Most Recent Model
            </div>
            <div className="text-sm sm:text-base font-semibold text-slate-100 truncate" data-testid="context-recent-project-name">
              {mostRecent?.name || "None yet"}
            </div>
          </div>
          <div className="col-span-2 sm:col-span-1">
            <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1">
              <Clock size={12} /> Last Touched
            </div>
            <div className="text-sm sm:text-base font-semibold text-slate-100" data-testid="context-last-touched-date">
              {mostRecent ? formatDate(mostRecent.updated_at) : "N/A"}
            </div>
          </div>
        </div>

        {projects.length === 0 ? (
          <div
            className="relative border-2 border-dashed border-slate-700/80 rounded-2xl p-8 sm:p-16 text-center bg-slate-900/40 backdrop-blur-sm overflow-hidden"
            data-testid="dashboard-empty-state"
          >
            <div className="absolute top-3 left-3 w-6 h-6 border-t-2 border-l-2 border-slate-600/60 rounded-tl-lg" />
            <div className="absolute top-3 right-3 w-6 h-6 border-t-2 border-r-2 border-slate-600/60 rounded-tr-lg" />
            <div className="absolute bottom-3 left-3 w-6 h-6 border-b-2 border-l-2 border-slate-600/60 rounded-bl-lg" />
            <div className="absolute bottom-3 right-3 w-6 h-6 border-b-2 border-r-2 border-slate-600/60 rounded-br-lg" />
            <FolderKanban className="mx-auto mb-4 text-slate-600" size={32} />
            <p className="text-slate-400 mb-4 relative z-10">No projects yet. Start blank, import a .archimate file, or let AI draft one from a document.</p>
            <Button
              onClick={() => setDialogOpen(true)}
              data-testid="dashboard-empty-new-project-button"
              className="bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-600/25 relative z-10"
            >
              Create your first project
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5" data-testid="projects-grid">
            {sortedProjects.map((p) => (
              <div
                key={p.id}
                data-testid={`project-card-${p.id}`}
                className="group relative rounded-xl border border-slate-800/80 bg-slate-900/80 p-5 hover:bg-slate-800/60 hover:border-blue-500/40 transition-all duration-200 cursor-pointer shadow-lg hover:shadow-[0_0_25px_-5px_rgba(37,99,235,0.25)] overflow-hidden"
                onClick={() => navigate(`/project/${p.id}`)}
              >
                <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-blue-500/0 group-hover:border-blue-500/50 transition-colors rounded-tl-xl" />
                <div className="h-9 w-9 rounded-lg bg-blue-950/60 border border-blue-500/20 flex items-center justify-center mb-3">
                  <LayoutGrid size={16} className="text-blue-400" />
                </div>
                <h3 className="font-heading text-base font-semibold text-slate-100 group-hover:text-blue-400 transition-colors mb-1 truncate">
                  {p.name}
                </h3>
                <p className="text-xs text-slate-500 font-mono">Updated {formatDate(p.updated_at)}</p>
                <button
                  data-testid={`delete-project-button-${p.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteTarget(p);
                  }}
                  className="absolute top-3.5 right-3.5 p-1.5 rounded-lg bg-slate-800/80 border border-slate-700/50 text-slate-400 hover:text-rose-400 hover:bg-rose-950/50 hover:border-rose-800/50 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-all"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
        )}
      </main>
      <NewProjectDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onCreated={load}
        onQuotaExceeded={(msg) => {
          setDialogOpen(false);
          setUpgradeReason(msg);
          setUpgradeOpen(true);
        }}
      />
      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} reason={upgradeReason} />
      <HelpCenter open={helpOpen} onOpenChange={setHelpOpen} />
      <SupportCenter open={supportOpen} onOpenChange={setSupportOpen} />
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-project-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>This permanently deletes the project and all its elements, relationships, views and chat history.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-project-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-project-confirm-button" onClick={handleDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
